import React from 'react';
import './MarketplacePracticeProfile.scss';
import MarketplacePracticeLocationsTable from './MarketplacePracticeLocationsTable/MarketplacePracticeLocationsTable';
import PracticeProfileImages from '../../PracticeProfileImages/PracticeProfileImages';
import { Input } from '@availity/form';
import { Col, FormGroup, Row } from 'reactstrap';
import {
    OwnershipType,
    OwnershipTypeOptions,
} from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import {
    RevenueGrowthRate,
    RevenueGrowthRates,
} from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { PracticeProfileInfo } from '../../../slices/profiles-data-slice/models/practice-profile-info';
import { useFormikContext, getIn } from 'formik';
import {
    DataSynchronizationValidator,
    SyncField,
} from '../../DataSynchronizationValidator/DataSynchronizationValidator';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    isOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { StarRating } from '../../StarRating/StarRating';

interface MarketplacePracticeProfileProps {
    practice: PracticeProfileInfo;
    profileId: string;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    onAddLocationClicked(): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    isAdmin?: boolean;
    isBroker?: boolean;
    dataSyncTrigger?: number;
}

export const MarketplacePracticeProfile = (
    props: MarketplacePracticeProfileProps
) => {
    const { isEditable = true, isAdmin = false, isBroker = false } = props;
    const canEditRatings = isAdmin || isBroker;
    const practice = props.practice;
    const serviceFocuses = useServiceFocuses();
    const { values, errors } = useFormikContext<{ ratings?: { google?: number; facebook?: number; yelp?: number; googleReviewCount?: number; facebookReviewCount?: number; yelpReviewCount?: number } }>();

    const formatRatingDisplay = (v: unknown): string => {
        if (v === '' || v == null) return '';
        const n = Number(v);
        if (isNaN(n)) return '';
        const clamped = Math.min(5, Math.max(0, n));
        return clamped % 1 === 0 ? clamped.toString() : clamped.toFixed(1);
    };

    const isOtherPracticeFocusInputVisible = () => {
        return isOtherFocus(
            serviceFocuses,
            getIn(values, `${props.fieldPrefix}.practiceFocusType`)
        );
    };

    const renderDataSynchronizationValidator = () => {
        if (isBroker) {
            return null;
        }

        const currentPractice = getIn(values, props.fieldPrefix);
        const practiceId = currentPractice?.practiceId || practice.practiceId;
        const fteDvms = currentPractice?.fteDvms || practice.fteDvms || 0;

        if (!practiceId) {
            return null;
        }

        return (
            <DataSynchronizationValidator
                key={`practice-sync-validator-${practice.id}`}
                practiceId={practiceId}
                fteDvm={fteDvms}
                ltmRevenue={0}
                sync={props.dataSyncTrigger}
                fieldsToSync={[SyncField.FteDvm]}
            />
        );
    };

    return (
        <div className="marketplace-practice-profile">
            <section className="practice-information">
                <div className="practice-form-container">
                    <Row>
                        <Col md={6} className="practice-form">
                            <div className="section-title mb-4">
                                <h2>Practice Information</h2>
                            </div>
                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Practice Name
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Owner Name
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.ownerFullName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Ownership Type
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceOwnershipType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                OwnershipType.SingleOwner
                                            }>
                                            {Object.entries(
                                                OwnershipTypeOptions
                                            ).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Service Focus
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceFocusType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={1}>
                                            {serviceFocuses.map((option) => (
                                                <option
                                                    key={option.id}
                                                    value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hidden={!isOtherPracticeFocusInputVisible()}>
                                <Row>
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Specify Service Focus
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.otherPracticeType`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Number of FTE DVMs
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.fteDvms`}
                                            type="number"
                                            className="form-control"
                                            readOnly={true}
                                            disabled={!isEditable}
                                            defaultValue={0}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Revenue Growth Rate
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.revenueGrowthRate`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                RevenueGrowthRate.noGrowth
                                            }>
                                            {RevenueGrowthRates.map(
                                                (option) => (
                                                    <option
                                                        key={option.id}
                                                        value={option.id}>
                                                        {option.name}
                                                    </option>
                                                )
                                            )}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            {(canEditRatings || getIn(values, `${props.fieldPrefix}.ratings.google`) != null) && (
                            <>
                                {!canEditRatings && (
                                    <FormGroup className="star-rating-form-group">
                                        <Row className="horizontal-form-group">
                                            <Col md={3} className="col-form-label" tag="label">
                                                Google Rating
                                            </Col>
                                            <Col md={7}>
                                                <StarRating
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.google`)}
                                                    size="md"
                                                    reviewCount={getIn(values, `${props.fieldPrefix}.ratings.googleReviewCount`)}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {canEditRatings && (
                                    <FormGroup className="rating-row-group">
                                        <div className="rating-row">
                                            <div className="rating-field">
                                                <label className="rating-field__label">Google rating</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.google`}
                                                    type="number"
                                                    step="0.1"
                                                    min={0}
                                                    max={5}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={formatRatingDisplay(
                                                        getIn(values, `${props.fieldPrefix}.ratings.google`)
                                                    )}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.google`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.google`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {getIn(errors, `${props.fieldPrefix}.ratings.google`) && (
                                                    <div className="invalid-feedback d-block">
                                                        {getIn(errors, `${props.fieldPrefix}.ratings.google`)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rating-field">
                                                <label className="rating-field__label">Google # of reviews</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.googleReviewCount`}
                                                    type="number"
                                                    step="1"
                                                    min={0}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.googleReviewCount`) ?? ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.googleReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.googleReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}
                            </>
                            )}
                            {(canEditRatings || getIn(values, `${props.fieldPrefix}.ratings.facebook`) != null) && (
                            <>
                                {!canEditRatings && (
                                    <FormGroup className="star-rating-form-group">
                                        <Row className="horizontal-form-group">
                                            <Col md={3} className="col-form-label" tag="label">
                                                Facebook Rating
                                            </Col>
                                            <Col md={7}>
                                                <StarRating
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.facebook`)}
                                                    size="md"
                                                    reviewCount={getIn(values, `${props.fieldPrefix}.ratings.facebookReviewCount`)}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {canEditRatings && (
                                    <FormGroup className="rating-row-group">
                                        <div className="rating-row">
                                            <div className="rating-field">
                                                <label className="rating-field__label">Facebook rating</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.facebook`}
                                                    type="number"
                                                    step="0.1"
                                                    min={0}
                                                    max={5}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={formatRatingDisplay(
                                                        getIn(values, `${props.fieldPrefix}.ratings.facebook`)
                                                    )}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.facebook`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.facebook`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {getIn(errors, `${props.fieldPrefix}.ratings.facebook`) && (
                                                    <div className="invalid-feedback d-block">
                                                        {getIn(errors, `${props.fieldPrefix}.ratings.facebook`)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rating-field">
                                                <label className="rating-field__label">Facebook # of reviews</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.facebookReviewCount`}
                                                    type="number"
                                                    step="1"
                                                    min={0}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.facebookReviewCount`) ?? ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.facebookReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.facebookReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}
                            </>
                            )}
                            {(canEditRatings || getIn(values, `${props.fieldPrefix}.ratings.yelp`) != null) && (
                            <>
                                {!canEditRatings && (
                                    <FormGroup className="star-rating-form-group">
                                        <Row className="horizontal-form-group">
                                            <Col md={3} className="col-form-label" tag="label">
                                                Yelp Rating
                                            </Col>
                                            <Col md={7}>
                                                <StarRating
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.yelp`)}
                                                    size="md"
                                                    reviewCount={getIn(values, `${props.fieldPrefix}.ratings.yelpReviewCount`)}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {canEditRatings && (
                                    <FormGroup className="rating-row-group">
                                        <div className="rating-row">
                                            <div className="rating-field">
                                                <label className="rating-field__label">Yelp rating</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.yelp`}
                                                    type="number"
                                                    step="0.1"
                                                    min={0}
                                                    max={5}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={formatRatingDisplay(
                                                        getIn(values, `${props.fieldPrefix}.ratings.yelp`)
                                                    )}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.yelp`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.yelp`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {getIn(errors, `${props.fieldPrefix}.ratings.yelp`) && (
                                                    <div className="invalid-feedback d-block">
                                                        {getIn(errors, `${props.fieldPrefix}.ratings.yelp`)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rating-field">
                                                <label className="rating-field__label">Yelp # of reviews</label>
                                                <Input
                                                    name={`${props.fieldPrefix}.ratings.yelpReviewCount`}
                                                    type="number"
                                                    step="1"
                                                    min={0}
                                                    className="form-control"
                                                    disabled={!canEditRatings}
                                                    value={getIn(values, `${props.fieldPrefix}.ratings.yelpReviewCount`) ?? ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.yelpReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        props.setFormFieldValue(
                                                            `${props.fieldPrefix}.ratings.yelpReviewCount`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}
                            </>
                            )}
                            {!canEditRatings && getIn(values, `${props.fieldPrefix}.ratings.averageRating`) != null && (
                                <FormGroup className="star-rating-form-group">
                                    <Row className="horizontal-form-group">
                                        <Col md={3} className="col-form-label" tag="label">
                                            Average Rating
                                        </Col>
                                        <Col md={7}>
                                            <StarRating
                                                value={getIn(values, `${props.fieldPrefix}.ratings.averageRating`)}
                                                size="md"
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}
                            {canEditRatings && (
                            <FormGroup className="star-rating-form-group">
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Average Rating
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.ratings.averageRating`}
                                            type="number"
                                            step="0.1"
                                            min={0}
                                            max={5}
                                            className="form-control"
                                            value={formatRatingDisplay(
                                                getIn(values, `${props.fieldPrefix}.ratings.averageRating`)
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${props.fieldPrefix}.ratings.averageRating`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${props.fieldPrefix}.ratings.averageRating`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {getIn(errors, `${props.fieldPrefix}.ratings.averageRating`) && (
                                            <div className="invalid-feedback d-block">
                                                {getIn(errors, `${props.fieldPrefix}.ratings.averageRating`)}
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </FormGroup>
                            )}
                        </Col>
                        <Col md={6} className="practice-form">
                            <div className="section-title mb-4">
                                <h2>Practice Images</h2>
                            </div>
                            <PracticeProfileImages
                                key={`${practice.id}-${
                                    (practice.uploads || []).length
                                }`}
                                images={practice.uploads || []}
                                profileId={props.profileId}
                                practiceProfileId={practice.id}
                                maxImages={10}
                                disabled={!isEditable}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup className="description-container">
                                <label>Description</label>
                                <Input
                                    name={`${props.fieldPrefix}.description`}
                                    type="textarea"
                                    rows={8}
                                    className="form-control"
                                    disabled={!isEditable}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </section>

            {practice.practiceLocations?.length ? (
                <MarketplacePracticeLocationsTable
                    locations={practice.practiceLocations}
                    fieldPrefix={`${props.fieldPrefix}.practiceLocations`}
                    setFormFieldValue={props.setFormFieldValue}
                    removeLocation={props.removeLocation}
                    isEditable={isEditable}
                />
            ) : (
                <></>
            )}
            {isEditable && (
                <div
                    className="link-button"
                    onClick={props.onAddLocationClicked}>
                    {practice.practiceLocations?.length
                        ? '+ Add Another Location'
                        : '+ Add Location'}
                </div>
            )}
            {renderDataSynchronizationValidator()}
        </div>
    );
};
